import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageState from '../../components/PageState.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import Notice from '../../components/Notice.jsx';
import { api } from '../../lib/api.js';
import { customerSectionPath } from '../../lib/navigation.js';
import ReviewWorkspace from './ReviewWorkspace.jsx';
import { signedInAdapter } from './my-review-adapter.js';

/**
 * "Your reviews" for a signed-in admin who is also a reviewer (spec 5): the same list as the email link, inside the
 * portal, with the method 'signed-in' (no one-time code). A customer admin gets the whole campaign by person and by
 * role, and can hand a row to a colleague. The GET only reads; a decision is recorded only by Save.
 */
export default function MyReviewsPage({ customerId, showBack = true }) {
  const [load, setLoad] = useState({ status: 'loading', data: null, error: null });
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    api.getMyAccessReviews(customerId)
      .then((data) => { if (!cancelled) setLoad({ status: 'ready', data, error: null }); })
      .catch((e) => { if (!cancelled) setLoad({ status: 'error', data: null, error: e }); });
    return () => { cancelled = true; };
  }, [customerId, reloads]);

  // A reviewer-role member has no Access reviews page to go back to (showBack false): this is their landing.
  const back = showBack ? { to: customerSectionPath(customerId, 'access-reviews'), label: 'Access reviews' } : undefined;
  if (load.status === 'loading') return <><PageHeader title="Your review" back={back} /><PageState state="loading" header={false} cards={0} label="Loading your review" /></>;
  if (load.status === 'error') {
    return (
      <>
        <PageHeader title="Your review" back={back} />
        <PageState state="error" title="Your review could not be loaded" message={`${load.error?.body?.message ?? load.error?.message ?? 'The request failed'}.`} onRetry={reload} />
      </>
    );
  }
  const view = load.data;
  if (!view || view.state !== 'ready' || !Array.isArray(view.items) || view.items.length === 0) {
    return (
      <>
        <PageHeader title="Your review" back={back} />
        <Notice tone="info" title="Nothing to review" testId="my-reviews-empty">No assignment in an open review is waiting for your decision.</Notice>
      </>
    );
  }
  const adapter = signedInAdapter(customerId, view);
  return (
    <div className="space-y-5" data-testid="my-reviews">
      {back ? <Link to={back.to} className="inline-flex items-center gap-1 text-sm font-semibold text-ipc-light hover:underline">{back.label}</Link> : null}
      <ReviewWorkspace view={{ ...view, method: 'signed-in' }} adapter={adapter} onGone={reload} />
    </div>
  );
}
